import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Delete, Lock, ShieldCheck } from 'lucide-react';
import { cn } from '@attendance/ui/utils';
import { appLockApi } from '../../api/settings.api.js';
import { BrandLogo } from '../common/BrandLogo.js';

interface LockScreenProps {
  scope: 'open' | 'admin';
  title: string;
  subtitle?: string;
  onUnlock: () => void;
  /** Optional action under the keypad (e.g. Cancel / Sign out). */
  footer?: React.ReactNode;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;
const MAX_LEN = 8;
const MIN_LEN = 4;

/**
 * Full-screen minimalist PIN pad — Layer 1 (open the app) and Layer 2 (admin
 * settings) both render this. Verification is server-side (appLockApi.verify);
 * the PIN never persists. Physical keyboard works too (digits / Backspace /
 * Enter) for foreman phones and testing.
 */
export function LockScreen({ scope, title, subtitle, onUnlock, footer }: LockScreenProps): JSX.Element {
  const { t } = useTranslation();
  // pinRef is the synchronous source of truth (state just mirrors it for
  // render), so a fast keypad burst or pressing Enter on the same tick as the
  // last digit always submits the complete PIN rather than a stale closure.
  const pinRef = useRef('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const verifyingRef = useRef(false);

  const submit = useCallback(async (): Promise<void> => {
    const candidate = pinRef.current;
    if (candidate.length < MIN_LEN || verifyingRef.current) return;
    verifyingRef.current = true;
    setVerifying(true);
    try {
      const ok = await appLockApi.verify(scope, candidate);
      if (ok) {
        onUnlock();
        return;
      }
      setError(true);
      pinRef.current = '';
      setPin('');
    } catch {
      setError(true);
      pinRef.current = '';
      setPin('');
    } finally {
      verifyingRef.current = false;
      setVerifying(false);
    }
  }, [scope, onUnlock]);

  const press = useCallback((digit: string): void => {
    if (pinRef.current.length >= MAX_LEN) return;
    setError(false);
    pinRef.current += digit;
    setPin(pinRef.current);
  }, []);
  const backspace = useCallback(() => {
    setError(false);
    pinRef.current = pinRef.current.slice(0, -1);
    setPin(pinRef.current);
  }, []);

  // Physical keyboard support.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (/^[0-9]$/.test(e.key)) press(e.key);
      else if (e.key === 'Backspace') backspace();
      else if (e.key === 'Enter') void submit();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [press, backspace, submit]);

  const dotCount = Math.max(pin.length, MIN_LEN);

  return (
    <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center gap-8 bg-background px-gutter-sm md:px-gutter">
      <div className="flex flex-col items-center gap-component text-center">
        <BrandLogo size="lg" fallback="monogram" />
        <div className="space-y-tight">
          {/* `text-h2`, not `text-xl`. A section-heading step rather than the
              34px page title on purpose: this is a full-screen GATE, not a page
              in the shell — there is no eyebrow, no toolbar and nothing under it
              but a keypad, and 34px beside an 18px lock glyph reads as a banner
              rather than as a question. `text-h2` also brings the display face
              and its −0.35px tracking, which `text-xl` (Instrument Sans at
              default tracking) did not. */}
          <h1 className="flex items-center justify-center gap-element text-h2 text-foreground">
            {scope === 'admin' ? <ShieldCheck size={18} /> : <Lock size={18} />}
            {title}
          </h1>
          {subtitle && <p className="text-copy text-muted-foreground">{subtitle}</p>}
        </div>
      </div>

      {/* PIN dots. The empty dot is `bg-border-strong` rather than
          `bg-muted-foreground/25`: an alpha on ink computes against whatever is
          behind it, so the same class measured differently on the warm page than
          it would on a card, and nothing in the contrast guard could see either.
          `border-strong` is the system's own "a mark you are meant to notice but
          not read" value. The filled dot keeps the brand, and the error state
          keeps danger — never colour alone, which is what the sentence under
          the row is for. */}
      <div className="flex h-6 items-center gap-inline" aria-hidden>
        {Array.from({ length: dotCount }).map((_, i) => (
          <span
            key={i}
            className={cn(
              'h-3 w-3 rounded-full transition-colors duration-fast ease-standard',
              i < pin.length
                ? error
                  ? 'bg-danger'
                  : 'bg-primary'
                : 'bg-border-strong'
            )}
          />
        ))}
      </div>
      {/* `min-h-6`, not `h-4`: the line reserves its own space so the keypad
          does not jump when the message appears, and at `text-copy` (14px on a
          1.5 leading) a fixed 16px box clipped the descenders of the very
          message it exists to show. */}
      <p
        className={cn('min-h-6 text-copy', error ? 'text-danger' : 'text-transparent')}
        role="alert"
      >
        {error ? 'Incorrect PIN — try again' : 'placeholder'}
      </p>

      {/* Keypad */}
      <div className="grid grid-cols-3 gap-inline">
        {KEYS.map((k) => (
          <KeypadButton key={k} onClick={() => press(k)} disabled={verifying}>
            {k}
          </KeypadButton>
        ))}
        <KeypadButton onClick={backspace} disabled={verifying} aria-label={t('lock.backspace', 'Backspace')} variant="ghost">
          <Delete size={22} />
        </KeypadButton>
        <KeypadButton onClick={() => press('0')} disabled={verifying}>
          0
        </KeypadButton>
        <KeypadButton
          onClick={() => void submit()}
          disabled={verifying || pin.length < MIN_LEN}
          aria-label={t('lock.unlock', 'Unlock')}
          variant="primary"
        >
          <Lock size={20} />
        </KeypadButton>
      </div>

      {footer && <div className="pt-2">{footer}</div>}
    </div>
  );
}

function KeypadButton({
  children,
  onClick,
  disabled,
  variant = 'default',
  'aria-label': ariaLabel,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'default' | 'ghost' | 'primary';
  'aria-label'?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        /* GEOMETRY. 64×64 is kept exactly — the brief for this screen is "do not
         * shrink anything, the floor is 44×44", and a keypad is the one surface
         * in this app where a mis-tap costs a lockout. It is expressed as
         * `min-h-16 min-w-16` on a square rather than `h-16 w-16` so that the
         * key can only ever grow: `size-16` is a promise that 64px is enough for
         * whatever is inside, and the Backspace and Unlock keys carry glyphs
         * whose size is set elsewhere. A circle that clips its own glyph is
         * worse than a slightly larger circle. */
        'flex min-h-16 min-w-16 items-center justify-center rounded-full',
        /* `text-metric-sm` (26px Archivo 700 + tabular-nums) replaces
         * `text-2xl font-medium` (24px Instrument Sans 500). Digits on a keypad
         * are the most figure-like figures in the product, and tabular widths
         * are what keep 1 and 8 the same size in a 3×4 grid — with proportional
         * digits the "1" key's glyph sat visibly narrower than its neighbours. */
        'text-metric-sm',
        /* MOTION IS GATED ON `enabled:`. `transition` + `active:scale-95` used to
         * apply unconditionally, so the whole keypad animated its own fade to
         * 40% opacity the moment a verification started — twelve dead keys
         * easing out together, which reads as the app doing something for you
         * rather than as the app refusing input. A disabled or blocked control
         * never animates; motion promises something will happen. The press
         * squash stays on the keys that can still be pressed, because on a
         * keypad it is the only feedback that a tap registered. */
        'enabled:transition enabled:duration-fast enabled:ease-standard enabled:active:scale-95',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-40',
        variant === 'primary'
          ? 'bg-primary text-primary-foreground enabled:hover:bg-primary-hover'
          : variant === 'ghost'
            ? 'text-muted-foreground enabled:hover:bg-surface-2'
            : /* `ring-input`, not `ring-border`. On warm paper a key's `--surface`
               * fill is 1.043:1 against the page, so the ring is the ONLY thing
               * that says a key is there — and `--border` is 1.36:1, well under
               * the 3:1 WCAG 1.4.11 asks of a control boundary. `--input` is the
               * role the token contract names for exactly this: the faintest ink
               * the system allows, 3.56:1 on the page. This is the screen where
               * that difference is read at arm's length, outdoors, on a tablet. */
              'bg-surface text-foreground ring-1 ring-input enabled:hover:bg-surface-2'
      )}
    >
      {children}
    </button>
  );
}
